"use client";
import React from "react";
import OfferCard from "@/components/OfferCard";
import AdSlot from "@/helpers/gpt";
import { adsScript } from "@/utils/adsTempConstant";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import useMediaQuery from "@/utils/hooks/useMediaQuery";
import { ProductType } from "@/utils/types/product";
import useGoogleAdsReload from "@/utils/hooks/useGoogleAdsReload";
import { Box, Button, Columns, Row, Tag, Text } from "@dxp-feed/ui";
import {
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  NO_FILTERED_RESULTS_PROP_FOR_POPULAR_PRODUCTS_SLIDER,
  SHORTING_PRODUCTS,
} from "@/utils/constant";
import { useAlgoliaViewAnalyticsEvent } from "@/utils/hooks/useAlgoliaViewAnalyticsEvent";
import { useHits, usePagination, useSortBy } from "@dxp-feed/algolia";
import { dataLayerProductViewList } from "@/helpers/dataLayer";
import { setFavouriteMessage } from "@/utils/redux/favouriteProduct";
import { setShoppingMessage } from "@/utils/redux/shoppingList";
import { getItems } from "@/helpers/useCommonFunctions";
import PopularSliderSection from "@/templates/home/_components/Search/PopularSliderSection";
import { RefineFunctions, useRefine } from "@/templates/provider/RefineContext";
import { setFilters } from "@/utils/redux/productsListing";
import { usePathname, useSearchParams } from "next/navigation";
import PageContainer from "@/components/Common/PageContainer";
import HightlightMap from "./HighlightMap";
import parse from "html-react-parser";
import { useTrendingItems } from "@/utils/hooks/useTrendingItems";

interface ProductGridProps {
  isSearchPage: boolean;
  gridClass?: string;
  searchQuery?: string;
  toggleFilter?: () => void;
  toggleCategory?: () => void;
  toggleSort?: () => void;
  isBrandPage?: boolean;
}

export const ProductGrid = ({
  isSearchPage,
  gridClass,
  searchQuery = "",
  toggleFilter,
  toggleCategory,
  toggleSort,
  isBrandPage = false,
}: ProductGridProps) => {
  const { items: hitsItems } = useHits();
  const { data: popularItems, isLoading: popularItemsLoading } =
    useTrendingItems(hitsItems.length === 0);

  const [items, setItems] = useState(
    getItems(searchQuery, isSearchPage, hitsItems, popularItems?.products?.hits)
  );
  const { algoliaLoading } = useAppSelector((state) => state.algoliaDataSlice);
  const { subcategory, showHighlight, filter } = useAppSelector(
    (state) => state.productsSlice
  );

  const { refineFunctions } = useRefine();

  const handleProductFilterRemove = (
    filterType: keyof RefineFunctions,
    itemName: string,
    itemValue: string
  ) => {
    if (refineFunctions[filterType]) {
      if (filterType != "flyer") {
        dispatch(
          setFilters({
            filter: filterType,
            filterName: itemName,
            filterValue: itemValue,
          })
        );
      }

      if (filterType == "allergens") {
        refineFunctions[filterType]();
      } else {
        refineFunctions[filterType](itemValue);
      }
    }
  };

  const searchParams = useSearchParams();
  const sortParam = searchParams.get("sort");

  const { options, currentRefinement: sortRefinement } = useSortBy({
    items: SHORTING_PRODUCTS,
  });

  const heading: string = useMemo(() => {
    return options.find((option) => option.value === sortRefinement)
      ?.label as string;
  }, [options, sortRefinement]);

  const sortLabel = "Select Sorting...";

  useEffect(() => {
    setItems(
      getItems(
        searchQuery,
        isSearchPage,
        hitsItems,
        popularItems?.products?.hits
      )
    );
  }, [isSearchPage, searchQuery, hitsItems, popularItems]);
  const pathname = usePathname();
  const { itemOrder, NoProductFoundHeading, NoProductFoundText } =
    useAppSelector((state) => state.getProductAemDataSlice);

  const [columnsx, setColumnsx] = useState(5);
  const innerWidth = useMediaQuery();
  const path = useAppSelector((state) => state?.adsSclice?.page ?? "");
  const gridRef = useAlgoliaViewAnalyticsEvent(
    items,
    GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT
  );
  const getColSpanClass = (columns: number) => {
    const colSpanMap: Record<number, string> = {
      1: "col-span-1",
      2: "col-span-2",
      3: "col-span-3",
      4: "col-span-4",
      5: "col-span-5",
    };
    return colSpanMap[columns] || "";
  };

  const getColumns = useCallback(() => {
    const width = innerWidth ?? 0;
    if (width >= 1400) return 5;
    if (width >= 1200) return 4;
    if (width >= 768) return 3;
    if (width >= 414) return 2;
    return 1;
  }, [innerWidth]);
  const dispatch = useAppDispatch();

  const { currentRefinement } = usePagination();

  useEffect(() => {
    setColumnsx(getColumns());
  }, [innerWidth, getColumns]);

  // Refresh ads when items change (pagination change)
  useGoogleAdsReload([adsScript?.[path]?.leaderboardBottom_A?.id], [items]);
  // useEffect(() => {
  //   if (window.googletag && isSearchPage) {
  //     console.log("Refreshing specific Ad on Pagination Change...");

  //     window.googletag.cmd.push(() => {
  //       const slot: any = window.googletag
  //         .pubads()
  //         .getSlots()
  //         .find(
  //           (s: any) => s.getSlotElementId() === "div-gpt-ad-1738640713531-0"
  //         );

  //       if (slot) {
  //         window.googletag.pubads().refresh([slot]);
  //       } else {
  //         console.warn("Ad slot not found!");
  //       }
  //     });
  //   }
  // }, [items]);

  useEffect(() => {
    dispatch(setFavouriteMessage());
    dispatch(setShoppingMessage());
    // Reset tracking when refinement changes
    setVisibleItems(new Set());
    setPendingItems(new Set());
    setIsAllProductCapture(false);
    setLastSentItems(new Set());
  }, [currentRefinement, searchQuery]);

  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const [pendingItems, setPendingItems] = useState<Set<string>>(new Set());
  const [isAllProductCapture, setIsAllProductCapture] = useState(false);
  const [lastSentItems, setLastSentItems] = useState<Set<string>>(new Set());

  const handleVisibilityChange = (item: any, isVisible: boolean) => {
    const itemString = JSON.stringify(item);

    if (isAllProductCapture) return;

    if (isVisible) {
      setVisibleItems((prev) => {
        const newVisible = new Set(prev);
        newVisible.add(itemString);
        return newVisible;
      });
      setPendingItems((prev) => {
        const newPending = new Set(prev);
        newPending.delete(itemString);
        return newPending;
      });
    } else {
      setPendingItems((prev) => {
        const newPending = new Set(prev);
        newPending.add(itemString);
        return newPending;
      });
      setVisibleItems((prev) => {
        const newVisible = new Set(prev);
        newVisible.delete(itemString);
        return newVisible;
      });
    }
  };

  useEffect(() => {
    if (isAllProductCapture || visibleItems.size === 0) return;
    const newItems = new Set(
      [...visibleItems].filter((x) => !lastSentItems.has(x))
    );
    if (newItems.size > 0) {
      const products = Array.from(newItems).map((item) => JSON.parse(item));
      dataLayerProductViewList({
        event: "view_item_list",
        ecommerce: {
          item_list_id: !isSearchPage ? "2" : "7",
          item_list_name: !isSearchPage ? "All products" : "Search - Products",
          items: products,
        },
      });
      setLastSentItems((prev) => new Set([...prev, ...newItems]));
    }
    if (visibleItems.size === items.length) {
      setIsAllProductCapture(true);
    }
  }, [
    visibleItems,
    isAllProductCapture,
    items.length,
    lastSentItems,
    searchQuery,
  ]);

  return (
    <>
      {items?.length ? (
        <>
          <Box as="div">
            {!isSearchPage && (
              <>
                <Box as="div" className="mb-[24px]">
                  {pathname !== "/flyer" && (
                    <Row className="items-center">
                      <Columns className="col-12">
                        <Text
                          as="h2"
                          className="!text-brandBlack font-extrabold capitalize !font-sans !text-3xl"
                        >
                          {subcategory}
                        </Text>
                      </Columns>
                    </Row>
                  )}
                </Box>

                {showHighlight && !isBrandPage && (
                  <PageContainer
                    layout={itemOrder}
                    componentMap={HightlightMap}
                  />
                )}
              </>
            )}
            {!!searchQuery === isSearchPage && (
              <Box
                as="div"
                className='relative filter-mobile flex flex-col-reverse sm:flex-row justify-between w-full gap-2 lg:!hidden bg-grey100 py-4 mb-6 before:block before:content-[" "] before:absolute before:top-0 before:left-[-24px] before:right-[-24px] 2md:before:left-[-48px] 2md:before:right-[-48px] before:bottom-0 before:bg-grey100'
              >
                <Box
                  as="div"
                  className="block 4xs:flex 4xs:flex-row gap-[6px] xs:gap-[10px]"
                >
                  <Button
                    variant="outlneWhite"
                    aria-label="filter icon"
                    onClick={toggleFilter}
                    //disabled={items?.length > 0 ? false : true}
                    className={`w-full sm:w-auto border bg-brandWhite border-grey350 font-light text-[11px] 3xs:text-sm 2xs:text-body gap-1 2xs:gap-2`}
                  >
                    <Text
                      as="span"
                      className="icon-filter w-[13px] h-[9px] text-[9px] 2xs:w-[13px] 2xs:h-[9px] 2xs:text-[9px] flex items-center justify-center text-grey950"
                    ></Text>
                    <Text as="span">
                      Filters
                      <Text as="span" className="ml-1">
                        {filter.length > 0 && `(${filter.length})`}
                      </Text>
                    </Text>
                  </Button>
                  <Button
                    variant="outlneWhite"
                    aria-label="swap button"
                    onClick={toggleSort}
                    disabled={items?.length > 0 ? false : true}
                    className={`mt-2 4xs:mt-0 w-full sm:w-auto border bg-brandWhite border-grey350 font-light text-[11px] 3xs:text-sm 2xs:text-body gap-1 2xs:gap-2 ${items?.length > 0 ? "" : "text-grey550 hover:text-grey550 !bg-grey300"}`}
                  >
                    <Text
                      as="span"
                      className="icon-swap w-[12px] h-[12px] text-[12px] 2xs:w-[18px] 2xs:h-[18px] 2xs:text-[18px] flex items-center justify-center text-grey950"
                    ></Text>
                    {sortParam || isSearchPage ? heading : sortLabel}
                  </Button>
                </Box>
                <Box as="div" className="flex">
                  <Button
                    variant="outlneWhite"
                    aria-label="All Category"
                    onClick={toggleCategory}
                    disabled={items?.length > 0 ? false : true}
                    className={`w-full sm:w-auto border bg-brandWhite border-grey350 font-light text-[11px] 3xs:text-sm 2xs:text-body gap-1 2xs:gap-2 ${items?.length > 0 ? "" : "text-grey550 hover:text-grey550 !bg-grey300"}`}
                  >
                    <Text
                      as="span"
                      className="icon-grid w-[10px] h-[10px] text-[10px] 2xs:w-[15px] 2xs:h-[15px] 2xs:text-[15px] flex items-center justify-center text-grey950"
                    ></Text>
                    All Category
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
          <Box
            as="div"
            className={`${gridClass ?? "grid grid-cols-1 2xs:grid-cols-2 md:grid-cols-3 2lg:grid-cols-4 3xl:grid-cols-5 gap-6"}`}
            ref={gridRef}
          >
            {items.map((item: ProductType, index: number) => {
              const showAd = isSearchPage && (index + 1) % (columnsx * 3) === 0;
              return (
                <React.Fragment key={item.objectID}>
                  <span data-object-id={item.objectID}>
                    <OfferCard
                      isSearchPage={isSearchPage}
                      item={item}
                      onVisibilityChange={handleVisibilityChange}
                      pageId={currentRefinement + 1}
                      viewItemList={
                        !isSearchPage ? "All products" : "Search - Products"
                      }
                      viewItemListId={!isSearchPage ? "2" : "7"}
                    />
                  </span>
                  {showAd && (
                    <AdSlot
                      key={`ad-${index}-${columnsx}-${path}-${items.length}`}
                      id={adsScript?.[path]?.leaderboardBottom_A?.id}
                      className={`${getColSpanClass(columnsx)} text-center mx-auto`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>{" "}
        </>
      ) : null}

      {!items?.length &&
        (!algoliaLoading || (isSearchPage && !searchQuery)) && (
          <Box as="div" className="flex items-center h-full w-full pt-6">
            <Box as="div" className="inline-block w-full">
              <Text
                as="h1"
                className="text-3xl font-sans text-brandBlack font-extraBold text-center"
              >
                {parse(NoProductFoundHeading) ??
                  "No products match your selected filters."}
              </Text>
              {filter?.length > 0 && (
                <Box className="flex justify-center gap-2 mt-4">
                  {filter.map((item, index) => (
                    <Tag
                      key={index}
                      variant="secondary"
                      className="!rounded-[4px] px-2 !py-1 !bg-[rgba(122,122,122,0.12)] backdrop-blur-[5px] flex gap-1"
                    >
                      {item.filterName}
                      <Button
                        variant="icon"
                        size="none"
                        aria-label="close button"
                        onClick={() => {
                          handleProductFilterRemove(
                            item.filter,
                            item.filterName,
                            item.filterValue
                          );
                        }}
                      >
                        <Text
                          as="span"
                          className="icon-close text-black text-[14px] font-bold cursor-pointer w-6 h-6 flex items-center justify-center"
                        ></Text>
                      </Button>
                    </Tag>
                  ))}
                </Box>
              )}

              <Text
                as="p"
                className="mt-4 mb-6 text-black text-body text-center"
              >
                {parse(NoProductFoundText) ??
                  "Try adjusting your options to discover new possibilities!"}
              </Text>

              {!!searchQuery === isSearchPage && (
                <Box
                  as="div"
                  className='relative filter-mobile flex flex-col-reverse sm:flex-row justify-between w-full gap-2 lg:!hidden bg-grey100 py-4 mb-6 before:block before:content-[" "] before:absolute before:top-0 before:left-[-24px] before:right-[-24px] 2md:before:left-[-48px] 2md:before:right-[-48px] before:bottom-0 before:bg-grey100'
                >
                  <Box
                    as="div"
                    className="block 4xs:flex 4xs:flex-row gap-[6px] xs:gap-[10px]"
                  >
                    <Button
                      aria-label={`Filters ${filter.length > 0 && `(${filter.length})`}`}
                      variant="outlneWhite"
                      onClick={toggleFilter}
                      // disabled={items?.length > 0 ? false : true}
                      className={`w-full sm:w-auto border bg-brandWhite border-grey350 font-light text-[11px] 3xs:text-sm 2xs:text-body gap-1 2xs:gap-2`}
                    >
                      <Text
                        as="span"
                        className="icon-filter w-[13px] h-[9px] text-[9px] 2xs:w-[13px] 2xs:h-[9px] 2xs:text-[9px] flex items-center justify-center text-grey950"
                      ></Text>
                      <Text as="span">
                        Filters
                        <Text as="span" className="ml-1">
                          {filter.length > 0 && `(${filter.length})`}
                        </Text>
                      </Text>
                    </Button>
                    <Button
                      variant="outlneWhite"
                      aria-label={
                        sortParam || isSearchPage ? heading : sortLabel
                      }
                      onClick={toggleSort}
                      disabled={items?.length > 0 ? false : true}
                      className={`mt-2 4xs:mt-0 w-full sm:w-auto border bg-brandWhite border-grey350 font-light text-[11px] 3xs:text-sm 2xs:text-body gap-1 2xs:gap-2 ${items?.length > 0 ? "" : "text-grey550 hover:text-grey550 !bg-grey300"}`}
                    >
                      <Text
                        as="span"
                        className="icon-swap w-[12px] h-[12px] text-[12px] 2xs:w-[18px] 2xs:h-[18px] 2xs:text-[18px] flex items-center justify-center text-neutral900"
                      ></Text>
                      {sortParam || isSearchPage ? heading : sortLabel}
                    </Button>
                  </Box>
                  <Box as="div" className="flex">
                    <Button
                      variant="outlneWhite"
                      onClick={toggleCategory}
                      disabled={items?.length > 0 ? false : true}
                      aria-label="All Category"
                      className={`w-full sm:w-auto border bg-brandWhite border-grey350 font-light text-[11px] 3xs:text-sm 2xs:text-body gap-1 2xs:gap-2 ${items?.length > 0 ? "" : "text-grey550 hover:text-grey550 !bg-grey300"}`}
                    >
                      <Text
                        as="span"
                        className="icon-grid w-[10px] h-[10px] text-[10px] 2xs:w-[15px] 2xs:h-[15px] 2xs:text-[15px] flex items-center justify-center text-neutral900"
                      ></Text>
                      All Category
                    </Button>
                  </Box>
                </Box>
              )}

              {!popularItemsLoading && (
                <Box as="div" className="w-full">
                  <PopularSliderSection
                    {...NO_FILTERED_RESULTS_PROP_FOR_POPULAR_PRODUCTS_SLIDER}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
    </>
  );
};
